"use client";

import * as React from "react";
import { format } from "date-fns";
import { srLatn } from "date-fns/locale";
import { toZonedTime } from "date-fns-tz";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import {
  availabilityOptionMetaVariants,
  availabilityOptionVariants,
} from "./availability-option";

interface TimeGridProps {
  selectedTime: string | null;
  onTimeSelect?: (time: string) => void;
  hourlyAvailability?: {
    hour: number;
    minute: number;
    availableTherapists: number;
  }[];
}

interface TimeSlotPickerProps {
  slots: Date[];
  onSlotSelect: (slot: Date) => void;
  selectedSlot?: Date;
  showTimezone?: boolean;
  heading?: React.ReactNode;
}

const DEFAULT_TIMES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(
  (hour) => ({ hour, minute: 0, availableTherapists: 0 }),
);

const PERIODS = [
  { label: "Jutro", min: 0, max: 11 },
  { label: "Popodne", min: 12, max: 17 },
  { label: "Veče", min: 18, max: 23 },
];

export const TimeGrid = ({
  selectedTime,
  onTimeSelect,
  hourlyAvailability,
}: TimeGridProps) => {
  const [timezone, setTimezone] = React.useState("");
  React.useEffect(() => {
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);
  const timezoneLabel = timezone ? formatTimezoneLabel(timezone) : "";

  const times = React.useMemo(() => {
    if (!hourlyAvailability) return DEFAULT_TIMES;
    const available = hourlyAvailability.filter(
      (time) => time.availableTherapists > 0,
    );
    const selected = selectedTime
      ? hourlyAvailability.find(
          ({ hour, minute }) =>
            `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}` ===
            selectedTime,
        )
      : undefined;
    const selectedParts = selectedTime?.split(":").map(Number);
    const selectedUnavailable =
      selectedTime && !selected && selectedParts?.length === 2
        ? {
            hour: selectedParts[0] ?? 0,
            minute: selectedParts[1] ?? 0,
            availableTherapists: 0,
          }
        : selected?.availableTherapists === 0
          ? selected
          : undefined;
    const visible = available.length > 0 ? available : DEFAULT_TIMES;
    return selectedUnavailable &&
      !visible.some(
        ({ hour, minute }) =>
          hour === selectedUnavailable.hour &&
          minute === selectedUnavailable.minute,
      )
      ? [...visible, selectedUnavailable].sort(
          (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute),
        )
      : visible;
  }, [hourlyAvailability, selectedTime]);

  const groupedPeriods = PERIODS.map((period) => ({
    ...period,
    times: times.filter(({ hour }) => hour >= period.min && hour <= period.max),
  })).filter((group) => group.times.length > 0);

  return (
    <div className="space-y-3">
      {groupedPeriods.map((period) => (
        <div key={period.label}>
          <p className="text-muted-foreground mb-1.5 text-xs font-medium">
            {period.label}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {period.times.map(({ hour, minute, availableTherapists }) => {
              const count = hourlyAvailability ? availableTherapists : null;
              const timeStr = `${hour.toString().padStart(2, "0")}:${minute
                .toString()
                .padStart(2, "0")}`;
              const isSelected = selectedTime === timeStr;
              const isDisabled = count === 0;
              const state = isDisabled
                ? "unavailable"
                : isSelected
                  ? "selected"
                  : "available";
              const className = cn(
                availabilityOptionVariants({ state }),
                "flex flex-col items-center rounded-lg px-3 py-2.5",
                isDisabled &&
                  isSelected &&
                  "border-input border-dashed bg-background",
              );
              const content = (
                <>
                  <span>{timeStr}</span>
                  <span
                    className={cn(availabilityOptionMetaVariants({ state }))}
                  >
                    {count === null ? (
                      <Skeleton
                        className={cn(
                          "mx-auto h-3 w-14",
                          isSelected && "opacity-30",
                        )}
                      />
                    ) : isDisabled ? (
                      isSelected ? (
                        "Nema terapeuta"
                      ) : (
                        <span className="invisible">0 terapeuta</span>
                      )
                    ) : (
                      <>
                        {count} {count === 1 ? "terapeut" : "terapeuta"}
                      </>
                    )}
                  </span>
                </>
              );

              const hourAriaLabel =
                count === null
                  ? timeStr
                  : isDisabled
                    ? `${timeStr}, nema terapeuta${isSelected ? ", izabrano vreme" : ""}`
                    : `${timeStr}, ${count} terapeuta`;

              return (
                <button
                  key={timeStr}
                  type="button"
                  onClick={() => !isDisabled && onTimeSelect?.(timeStr)}
                  disabled={isDisabled}
                  aria-label={hourAriaLabel}
                  className={className}
                >
                  {content}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {timezoneLabel && (
        <p className="text-muted-foreground text-xs">
          Tvoja vremenska zona: {timezoneLabel}
        </p>
      )}
    </div>
  );
};

function getClientTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function getTimezoneCity(timezone: string) {
  return timezone.split("/")[1]?.replace(/_/g, " ") ?? timezone;
}

function formatTimezoneLabel(timezone: string): string {
  try {
    const label = Intl.DateTimeFormat("sr-Latn", {
      timeZone: timezone,
      timeZoneName: "long",
    })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
    return label ?? getTimezoneCity(timezone);
  } catch {
    return getTimezoneCity(timezone);
  }
}

const DEFAULT_TIMEZONE = "Europe/Belgrade";

export const TimeSlotPicker = ({
  slots,
  onSlotSelect,
  selectedSlot,
  showTimezone = true,
  heading,
}: TimeSlotPickerProps) => {
  const [timezone, setTimezone] = React.useState(DEFAULT_TIMEZONE);
  const [isClient, setIsClient] = React.useState(false);

  React.useEffect(() => {
    setTimezone(getClientTimezone());
    setIsClient(true);
  }, []);

  const timezoneLabel = isClient ? formatTimezoneLabel(timezone) : "";

  const groupedPeriods = React.useMemo(() => {
    const slotItems = slots
      .map((slot) => {
        const zonedSlot = toZonedTime(slot, timezone);
        return {
          slot,
          hour: zonedSlot.getHours(),
          label: format(zonedSlot, "HH:mm", { locale: srLatn }),
          testId: `time-slot-${format(zonedSlot, "HH-mm", { locale: srLatn })}`,
        };
      })
      .sort((a, b) => a.slot.getTime() - b.slot.getTime());

    return PERIODS.map((period) => ({
      ...period,
      slots: slotItems.filter(
        (item) => item.hour >= period.min && item.hour <= period.max,
      ),
    })).filter((group) => group.slots.length > 0);
  }, [slots, timezone]);

  return (
    <div className="space-y-3">
      {heading && <h3 className="font-medium capitalize">{heading}</h3>}
      {groupedPeriods.map((period) => (
        <div key={period.label}>
          <p
            className="bg-background text-muted-foreground sticky top-0 z-10 mb-1.5 py-1 text-xs font-medium md:static md:bg-transparent md:py-0"
            data-testid={`time-period-${period.label}`}
          >
            {period.label}
          </p>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-3 md:gap-2">
            {period.slots.map(({ slot, label, testId }) => {
              const isSelected = selectedSlot?.getTime() === slot.getTime();

              return (
                <button
                  key={slot.toISOString()}
                  type="button"
                  data-testid={testId}
                  onClick={() => onSlotSelect(slot)}
                  aria-label={`Termin u ${label}`}
                  className={availabilityOptionVariants({
                    state: isSelected ? "selected" : "available",
                    className:
                      "min-h-11 justify-start rounded-full px-4 text-left md:min-h-9 md:justify-center md:px-3 md:text-center",
                  })}
                >
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {showTimezone && timezoneLabel && (
        <p className="text-muted-foreground text-xs">
          Sva vremena su prikazana u tvojoj vremenskoj zoni ({timezoneLabel}).
        </p>
      )}
    </div>
  );
};
